import React from 'react';
import history from 'helpers/history';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getCurrentUser } from 'actions/userActions';
import axios from 'helpers/axios';
import config from 'helpers/config';
import User from 'models/UserModel';
import UserModal from 'components/modals/user/UserModal';
import moment from 'moment';
import Toggle from 'components/common/Toggle';
import UserFullName from 'components/common/UserFullName';
import { Plus, Edit, Users, Info, Calendar, Mail, Phone, CheckCircle, XCircle } from 'lucide-react';
import HoverTooltip from 'components/common/HoverTooltip';
import dateFormats from 'helpers/dateFormats';

interface Props {
    dispatch: Dispatch;
    currentUser: User;
}

interface State {
    users: User[];
    isLoading: boolean;
    isCreateUserModalOpen: boolean;
    isEditUserModalOpen: boolean;
    editableUserId: string | null;
}

class UsersListSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            users: [],
            isLoading: true,
            isCreateUserModalOpen: false,
            isEditUserModalOpen: false,
            editableUserId: null,
        };
    }

    componentDidMount() {
        this.getUsers();
    }

    getUsers() {
        axios.get(`${config.apiUrl}/user`).then((res) => {
            res.data.sort((a: User, b: User) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1));
            this.setState({ users: res.data });
        });
    }
    editUser(e: React.MouseEvent, userId: string) {
        e.stopPropagation();
        this.setState({ isEditUserModalOpen: true, editableUserId: userId });
    }

    // deleteUser(e: Event, id: string) {}

    modalClosed(userCreated?: boolean, userUpdated?: boolean) {
        this.setState({
            isCreateUserModalOpen: false,
            isEditUserModalOpen: false,
            editableUserId: null,
        });
        if (userCreated || userUpdated) {
            this.componentDidMount();
            this.props.dispatch<any>(getCurrentUser());
        }
    }

    getUserModal() {
        const { isCreateUserModalOpen, isEditUserModalOpen, editableUserId } = this.state;

        if (isEditUserModalOpen && !!editableUserId) {
            return (
                <UserModal
                    userId={editableUserId}
                    onClose={(userCreated?: boolean, userUpdated?: boolean) =>
                        this.modalClosed(userCreated, userUpdated)
                    }
                ></UserModal>
            );
        } else if (isCreateUserModalOpen) {
            return (
                <UserModal
                    onClose={(userCreated?: boolean, userUpdated?: boolean) =>
                        this.modalClosed(userCreated, userUpdated)
                    }
                ></UserModal>
            );
        }
    }

    goToUserDetails(userId: string) {
        history.push(`/user/${userId}`);
    }
    render() {
        const { users, isCreateUserModalOpen, isEditUserModalOpen } = this.state;
        const { currentUser } = this.props;

        const activeUsers = users.filter((user) => user.isActive);
        const inactiveUsers = users.filter((user) => !user.isActive);

        return (
            <div className="flex flex-col  bg-gray-50">
                {(isCreateUserModalOpen || isEditUserModalOpen) && this.getUserModal()}

                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Users</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
                            <p className="text-gray-600 mt-1">
                                Manage user accounts, permissions, and access levels for your repair shop.
                            </p>
                        </div>
                        <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            onClick={() => this.setState({ isCreateUserModalOpen: !isCreateUserModalOpen })}
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add New User</span>
                        </button>
                    </div>
                </div>

                <div className="flex-1 p-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <CheckCircle className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-semibold text-gray-600">Active Users</p>
                                    <p className="text-2xl font-bold text-gray-900">{activeUsers.length}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-red-100 rounded-lg">
                                    <XCircle className="h-6 w-6 text-red-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-semibold text-gray-600">Inactive Users</p>
                                    <p className="text-2xl font-bold text-gray-900">{inactiveUsers.length}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Users className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-semibold text-gray-600">Total Users</p>
                                    <p className="text-2xl font-bold text-gray-900">{users.length}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Users Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-blue-500 text-white">
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Contact
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Registration Date
                                        </th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {users?.map((user) => (
                                        <tr
                                            key={user.id}
                                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                                            onClick={() => this.goToUserDetails(user.id)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="flex-shrink-0 h-10 w-10">
                                                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                            <span className="text-sm font-semibold text-blue-600">
                                                                {user.firstName?.charAt(0)}
                                                                {user.lastName?.charAt(0)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {user.firstName} {user.lastName}
                                                        </div>
                                                        {user.isAdmin && (
                                                            <div className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full inline-block mt-1">
                                                                Administrator
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    <div
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="flex items-center mt-1 group hover:text-blue-600"
                                                    >
                                                        <Mail
                                                            className="h-4 w-4 text-gray-400 mr-2 group-hover:text-blue-600"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <a
                                                            href={`mailto:${user.email}`}
                                                            className="hover:underline"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            {user.email}
                                                        </a>
                                                    </div>
                                                    {user.phoneNumber && (
                                                        <div
                                                            className="flex items-center mt-1 group hover:text-blue-600"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <Phone
                                                                className="h-4 w-4 text-gray-400 mr-2 group-hover:text-blue-600"
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                            <a
                                                                href={`tel:${user.phoneNumber.replace(/\s/g, '')}`}
                                                                className="hover:underline"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                {user.phoneNumber}
                                                            </a>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center text-sm text-gray-900">
                                                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                                    <HoverTooltip
                                                        content={`Registered on ${moment(user.createdAt).format(
                                                            dateFormats.USA_DATETIME,
                                                        )}`}
                                                    >
                                                        {moment(user.createdAt).format(dateFormats.USA_DATE_LONG)}
                                                    </HoverTooltip>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    {user.isActive ? (
                                                        <span className="w-fit items-center px-2.5 py-0.5 rounded-full inline-flex text-sm font-semibold bg-green-100 text-green-800">
                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="w-fit items-center px-2.5 py-0.5 rounded-full inline-flex text-sm font-semibold bg-red-100 text-red-800">
                                                            <XCircle className="h-3 w-3 mr-1" />
                                                            Inactive
                                                        </span>
                                                    )}
                                                    {!user.isActive && user.dateDeactivated && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            Deactivated at{' '}
                                                            {moment(user.dateDeactivated).format(
                                                                dateFormats.USA_DATE_TIME_LONG,
                                                            )}
                                                            {!!user?.deactivatedById && (
                                                                <div>
                                                                    by <UserFullName userId={user?.deactivatedById} />
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* {!(user.isAdmin || user.id === currentUser.id) && (
                                                        <div className="mt-2">
                                                            <Toggle
                                                                onChange={() => {}}
                                                                value={user.isActive}
                                                                name="isUserActive"
                                                                disabled={true}
                                                            />
                                                        </div>
                                                    )} */}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={(e) => this.editUser(e, String(user.id))}
                                                        className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                                                        title="Edit User"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Empty State */}
                    {users?.length < 1 && !isCreateUserModalOpen && (
                        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-12 text-center">
                            <div className="text-gray-400 mb-4">
                                <Users className="h-12 w-12 mx-auto" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No users yet</h3>
                            <p className="text-gray-600 mb-6">
                                Create your first user account to get started with user management.
                            </p>
                            <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors mx-auto"
                                onClick={() => this.setState({ isCreateUserModalOpen: true })}
                            >
                                <Plus className="h-4 w-4" />
                                <span>Create User</span>
                            </button>
                        </div>
                    )}

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">User Management Guidelines</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Active users can log in and access the system based on their permissions
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Administrator users have full access to all system features and settings
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Inactive users cannot log in but their data is preserved in the system
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Click on any user row to view detailed information and edit permissions
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        User activation/deactivation is tracked with timestamps and responsible user
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    currentUser: state.user.currentUser,
});

export default connect(mapStateToProps)(UsersListSettings);
